import { Vector3, type Object3D, type Mesh, type BufferAttribute } from "three";

export interface Pt {
  x: number;
  z: number;
}

/**
 * 모델의 실제 바닥 윤곽(로컬 XZ 평면 convex hull)을 계산한다.
 * 바운딩박스(사각형)보다 타이트해서 베이스 둘레 여백을 줄인다.
 * 볼록껍질이라 깊게 오목한 형태는 근사되지만 부지 베이스(거의 볼록)엔 충분.
 */
export function computeFootprintHull(root: Object3D, marginM = 1.5): Pt[] {
  const pts: Pt[] = [];
  const v = new Vector3();
  root.updateWorldMatrix(true, true);
  root.traverse((obj) => {
    const mesh = obj as Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const pos = mesh.geometry.attributes.position as BufferAttribute | undefined;
    if (!pos) return;
    // mesh당 최대 ~200 정점 샘플 (대형 모델 성능 보호)
    const step = Math.max(1, Math.floor(pos.count / 200));
    for (let i = 0; i < pos.count; i += step) {
      v.fromBufferAttribute(pos, i);
      mesh.localToWorld(v);
      pts.push({ x: v.x, z: v.z });
    }
  });
  const hull = convexHull(pts);
  return marginM > 0 ? offsetOutward(hull, marginM) : hull;
}

/** Andrew's monotone chain — 2D convex hull (CCW). */
function convexHull(points: Pt[]): Pt[] {
  if (points.length < 3) return points;
  const pts = points.slice().sort((a, b) => a.x - b.x || a.z - b.z);
  const cross = (o: Pt, a: Pt, b: Pt) =>
    (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);
  const lower: Pt[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0)
      lower.pop();
    lower.push(p);
  }
  const upper: Pt[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]!;
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0)
      upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/** 폴리곤을 무게중심 기준으로 marginM 만큼 바깥으로 확장 (가장자리 z-fighting/틈 방지). */
function offsetOutward(poly: Pt[], marginM: number): Pt[] {
  if (poly.length < 3) return poly;
  const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
  const cz = poly.reduce((s, p) => s + p.z, 0) / poly.length;
  return poly.map((p) => {
    const dx = p.x - cx;
    const dz = p.z - cz;
    const len = Math.hypot(dx, dz) || 1;
    return { x: p.x + (dx / len) * marginM, z: p.z + (dz / len) * marginM };
  });
}
